import { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
} from '@mui/material'
import {
  DragIndicator,
  VisibilityOff,
  Visibility,
  Close,
} from '@mui/icons-material'
import { MONTHS } from './GridConfig'

function formatModValue(changeType, value) {
  if (value == null || value === 0) return '—'
  if (changeType === 'PercentOfMinInv') {
    return `×${value.toFixed(2)}`
  }
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(value))
  return value >= 0 ? `+${formatted}` : `−${formatted}`
}

function scopeLabel(scope, identifier) {
  switch (scope) {
    case 'AllProduct':
      return 'All Product'
    case 'BriteCode':
      return `Brite ${identifier}`
    case 'ItemNumber':
      return `Item ${identifier}`
    default:
      return scope
  }
}

function scopeColor(scope) {
  switch (scope) {
    case 'AllProduct':
      return 'primary'
    case 'BriteCode':
      return 'secondary'
    case 'ItemNumber':
      return 'info'
    default:
      return 'default'
  }
}

export default function ModificationRow({
  mod,
  onToggleIgnore,
  onRemove,
  dragHandleProps = {},
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handleRemove = () => {
    setConfirmOpen(false)
    onRemove?.(mod.id)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          opacity: mod.isIgnored ? 0.45 : 1,
          transition: 'opacity 0.2s',
          '&:hover': { backgroundColor: 'action.hover' },
          minHeight: 36,
        }}
      >
        <Box {...dragHandleProps} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
          <DragIndicator sx={{ fontSize: 16 }} />
        </Box>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            minWidth: 120,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: mod.isIgnored ? 'line-through' : 'none',
          }}
        >
          {mod.name}
        </Typography>

        <Chip
          label={scopeLabel(mod.scope, mod.identifier)}
          size="small"
          color={scopeColor(mod.scope)}
          variant="outlined"
          sx={{ fontSize: '0.65rem', height: 20 }}
        />

        <Box sx={{ display: 'flex', flex: 1, gap: 0, justifyContent: 'flex-end' }}>
          {MONTHS.map((m) => {
            const value = mod.monthValues?.[m.key] ?? mod.defaultValue ?? 0
            return (
              <Typography
                key={m.key}
                variant="caption"
                sx={{
                  width: 72,
                  textAlign: 'right',
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  color: value === 0 ? 'text.disabled' : 'text.primary',
                }}
              >
                {formatModValue(mod.changeType, value)}
              </Typography>
            )
          })}
        </Box>

        <Tooltip title={mod.isIgnored ? 'Restore modification' : 'Ignore modification'}>
          <IconButton size="small" onClick={() => onToggleIgnore?.(mod.id)}>
            {mod.isIgnored
              ? <Visibility sx={{ fontSize: 16 }} />
              : <VisibilityOff sx={{ fontSize: 16 }} />
            }
          </IconButton>
        </Tooltip>

        <Tooltip title="Remove modification">
          <IconButton size="small" onClick={() => setConfirmOpen(true)}>
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ fontSize: '0.9rem' }}>
          Remove "{mod.name}"?
        </DialogTitle>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button size="small" color="error" variant="contained" onClick={handleRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
